import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { LifeBuoy, Plus, Pencil, Trash2, Users, Eye, Building2, Ticket, DollarSign, BookOpen } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const ROLE_LABEL = { hd_admin: "Company Admin", hd_agent: "Agent", hd_user: "End User" };

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-white border border-neutral-200 rounded-lg p-4">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">{label}</p>
      <Icon size={15} className="text-neutral-400" />
    </div>
    <p className="text-2xl font-bold font-heading mt-1">{value}</p>
  </div>
);

export default function HelpdeskAdmin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [price, setPrice] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [busy, setBusy] = useState(false);
  const [membersOf, setMembersOf] = useState(null);
  const [members, setMembers] = useState(null);

  const load = () => {
    api.get("/admin/helpdesk/companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/helpdesk/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/helpdesk/pricing").then((r) => setPrice(r.data.monthly_price));
  };
  useEffect(() => { load(); }, []);

  const savePrice = async () => {
    try {
      await api.put("/admin/helpdesk/pricing", { monthly_price: parseFloat(price) });
      toast.success("Helpdesk pricing updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/helpdesk/companies", form);
      toast.success(`Company "${form.company_name}" created with a 14-day trial`);
      setShow(false);
      setForm({ company_name: "", admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const rename = async (c) => {
    const name = window.prompt("Company name", c.name);
    if (!name || name.trim().length < 2 || name === c.name) return;
    try { await api.put(`/admin/helpdesk/companies/${c.id}`, { name: name.trim() }); toast.success("Company renamed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}" permanently?\n\nThis removes ALL its members (${c.member_count}), tickets (${c.ticket_count}) and articles. This cannot be undone.`)) return;
    try { await api.delete(`/admin/helpdesk/companies/${c.id}`); toast.success(`${c.name} deleted`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const suspend = async (c) => {
    if (!window.confirm(`${c.suspended ? "Reactivate" : "Suspend"} "${c.name}"?`)) return;
    try {
      await api.put(`/admin/helpdesk/companies/${c.id}/suspend`, { suspended: !c.suspended });
      toast.success(`${c.name} ${c.suspended ? "reactivated" : "suspended"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openMembers = async (c) => {
    setMembersOf(c);
    setMembers(null);
    try { const { data } = await api.get(`/admin/helpdesk/companies/${c.id}/members`); setMembers(data); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const impersonate = async (m) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${m.id}`);
      setUser(data);
      toast.success(`Now viewing as ${data.name}`);
      navigate("/desk");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="helpdesk-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-heading flex items-center gap-3"><LifeBuoy size={26} /> Helpdesk</h1>
          <p className="text-neutral-500 mt-1">Companies, analytics and pricing for the GloriDesk product.</p>
        </div>
        <button data-testid="add-company-button" onClick={() => setShow(true)}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
          <Plus size={15} /> Add company
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="hd-analytics">
        <Stat icon={Building2} label="Companies" value={analytics?.companies ?? "—"} testId="hd-stat-companies" />
        <Stat icon={DollarSign} label="MRR" value={analytics ? `$${analytics.mrr.toFixed(0)}` : "—"} testId="hd-stat-mrr" />
        <Stat icon={Building2} label="Paid / Trial" value={analytics ? `${analytics.active_paid} / ${analytics.trials}` : "—"} testId="hd-stat-paid-trial" />
        <Stat icon={Users} label="Members" value={analytics?.members ?? "—"} testId="hd-stat-members" />
        <Stat icon={Ticket} label="Open / Total tickets" value={analytics ? `${analytics.tickets_open} / ${analytics.tickets_total}` : "—"} testId="hd-stat-tickets" />
        <Stat icon={BookOpen} label="KB articles" value={analytics?.kb_articles ?? "—"} testId="hd-stat-kb" />
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8 flex flex-wrap items-end gap-3" data-testid="hd-pricing-panel">
        <div>
          <label className="text-xs font-medium text-neutral-600">Monthly subscription price ($)</label>
          <input data-testid="hd-price-input" type="number" step="0.01" min="1" value={price}
            onChange={(e) => setPrice(e.target.value)} className={inputCls + " block mt-1 w-36"} />
        </div>
        <button data-testid="hd-price-save" onClick={savePrice}
          className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save price</button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        {companies.length === 0 ? <p className="p-6 text-sm text-neutral-400" data-testid="hd-companies-empty">No helpdesk companies yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Company", "Members", "Tickets", "Subscription", "Joined", "Actions"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}>
                  <td className="px-6 py-3 font-medium">{c.name}{c.suspended && <span className="ml-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">suspended</span>}</td>
                  <td className="px-6 py-3">{c.member_count}</td>
                  <td className="px-6 py-3">{c.ticket_count}</td>
                  <td className="px-6 py-3">
                    <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${c.subscription.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                      {c.subscription.status}{c.subscription.days_left != null && ` · ${c.subscription.days_left}d left`}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-neutral-400">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <button data-testid={`company-members-${c.id}`} onClick={() => openMembers(c)} title="View members & impersonate"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50"><Users size={12} /> Members</button>
                      <button data-testid={`company-rename-${c.id}`} onClick={() => rename(c)} title="Rename company"
                        className="p-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50"><Pencil size={12} /></button>
                      <button data-testid={`suspend-company-${c.id}`} onClick={() => suspend(c)}
                        className={`px-2.5 py-1.5 rounded-md text-xs font-medium border ${c.suspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
                        {c.suspended ? "Reactivate" : "Suspend"}
                      </button>
                      <button data-testid={`company-delete-${c.id}`} onClick={() => remove(c)} title="Delete company"
                        className="p-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {show && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="add-company-modal">
          <form onSubmit={createCompany} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-4">Add company</h3>
            <label className="text-sm font-medium text-neutral-700">Company name</label>
            <input data-testid="new-company-name" required minLength={2} value={form.company_name}
              onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Admin name</label>
            <input data-testid="new-company-admin-name" required value={form.admin_name}
              onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Admin email</label>
            <input data-testid="new-company-admin-email" type="email" required value={form.admin_email}
              onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Admin password (min 8)</label>
            <input data-testid="new-company-admin-password" required minLength={8} value={form.admin_password}
              onChange={(e) => setForm((f) => ({ ...f, admin_password: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <div className="flex gap-2 mt-6">
              <button data-testid="new-company-save" disabled={busy}
                className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {busy ? "Creating..." : "Create company"}
              </button>
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {membersOf && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="company-members-modal">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-lg w-full p-7 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h3 className="font-semibold font-heading text-lg">{membersOf.name} — members</h3>
              <button onClick={() => setMembersOf(null)} data-testid="company-members-close" className="text-neutral-400 hover:text-neutral-900">✕</button>
            </div>
            {!members ? <p className="text-sm text-neutral-400 mt-4">Loading...</p> : (
              <ul className="mt-4 space-y-2">
                {members.map((m) => (
                  <li key={m.id} data-testid={`company-member-${m.id}`}
                    className="flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{m.name}</p>
                      <p className="text-xs text-neutral-400 truncate">{m.email} · {ROLE_LABEL[m.role] || m.role}</p>
                    </div>
                    <button data-testid={`impersonate-member-${m.id}`} onClick={() => impersonate(m)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-100">
                      <Eye size={12} /> Login as
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
